import React from 'react'
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native'
import styles, { colors } from '../styles/global'
import UserTag from '../Tags/UserTag'
import moment from 'moment'
import Spinner from '../UIComponents/Spinner'
import { useSelector } from 'react-redux'
import { getDateFormat } from '../UIComponents/FloatModals/DateFormatPickerModal'
import { translate } from '../../i18n/TranslationService'
import { getUserPresentationData } from '../ContactsView/Utils/ContactsHelper'
import { isAiWorkflowStep } from './workflowStepHelper'
import Icon from '../Icon'

const WorkflowStep = ({ step, stepNumber, updatingStep, canMoveUp, canMoveDown, onMoveUp, onMoveDown }) => {
    const isMiddleScreen = useSelector(state => state.isMiddleScreen)
    const addedByData = getUserPresentationData(step.addedById)
    const reviewerData = getUserPresentationData(step.reviewerUid)
    const addedInfo = isMiddleScreen
        ? translate('User added on Date', {
              user: addedByData.shortName,
              date: moment(step.date).format(getDateFormat()),
          })
        : translate('Step added by User, Date', {
              user: addedByData.displayName,
              date: moment(step.date).format(getDateFormat()),
          })

    // For an AI step, what it runs matters more than who added it, so it leads the caption.
    const info = isAiWorkflowStep(step) && step.aiActionName ? `${step.aiActionName} • ${addedInfo}` : addedInfo

    return (
        <View style={localStyles.container}>
            <View style={localStyles.stepNumberContainer}>
                <Text style={[styles.subtitle1, { color: 'white' }]}>{stepNumber}</Text>
            </View>

            <View style={localStyles.description}>
                <Text style={[styles.body1, { color: colors.Text01 }]}>{step.description}</Text>
                <Text style={[styles.caption2, { color: colors.Text03 }]} numberOfLines={1}>
                    {info}
                </Text>
            </View>

            <View style={localStyles.sendToContainer}>
                {!isMiddleScreen && (
                    <Text style={[styles.body2, { color: colors.Text02 }]}>{`${translate('Send to')}:`}</Text>
                )}
                <View style={!isMiddleScreen && { marginLeft: 12 }}>
                    {!updatingStep ? (
                        <UserTag
                            user={{
                                displayName: reviewerData.displayName,
                                photoURL: getUserPresentationData(step.reviewerUid).photoURL,
                            }}
                            onlyPhoto={isMiddleScreen}
                        />
                    ) : (
                        <Spinner containerSize={24} spinnerSize={16} />
                    )}
                </View>
            </View>

            {(onMoveUp || onMoveDown) && (
                <View style={localStyles.orderButtons}>
                    <TouchableOpacity
                        accessibilityLabel={translate('Move workflow step up')}
                        disabled={!canMoveUp}
                        onPress={event => {
                            event.stopPropagation?.()
                            onMoveUp?.()
                        }}
                        style={localStyles.orderButton}
                    >
                        <Icon name="chevron-up" size={18} color={canMoveUp ? colors.Text02 : colors.Text04} />
                    </TouchableOpacity>
                    <TouchableOpacity
                        accessibilityLabel={translate('Move workflow step down')}
                        disabled={!canMoveDown}
                        onPress={event => {
                            event.stopPropagation?.()
                            onMoveDown?.()
                        }}
                        style={localStyles.orderButton}
                    >
                        <Icon name="chevron-down" size={18} color={canMoveDown ? colors.Text02 : colors.Text04} />
                    </TouchableOpacity>
                </View>
            )}
        </View>
    )
}

export default WorkflowStep

const localStyles = StyleSheet.create({
    container: {
        flexDirection: 'row',
        height: 64,
        alignItems: 'center',
        borderRadius: 4,
        borderWidth: 1,
        borderColor: colors.UtilityBlue150,
        paddingLeft: 5,
        paddingRight: 8,
        marginBottom: 16,
    },
    stepNumberContainer: {
        backgroundColor: colors.UtilityBlue200,
        borderRadius: 2,
        width: 28,
        height: 54,
        alignItems: 'center',
        justifyContent: 'center',
    },
    description: {
        flexDirection: 'column',
        marginLeft: 7,
    },
    sendToContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        marginLeft: 'auto',
        height: 24,
        borderLeftWidth: 1,
        borderLeftColor: colors.Grey200,
        paddingLeft: 12,
    },
    orderButtons: {
        marginLeft: 8,
        height: 48,
        justifyContent: 'space-between',
    },
    orderButton: {
        width: 24,
        height: 24,
        alignItems: 'center',
        justifyContent: 'center',
    },
})
